import LaptopUp from '../../../assets/icons/Laptopset -up-1.svg';
import LaptopUp1 from '../../../assets/icons/Laptopset -up (1).svg';
import LaptopUp2 from '../../../assets/icons/Laptopset -up-2.svg';
import MobileImage from '../../../assets/images/Main body.png';
import mobileLaptopUp from '../../../assets/images/mobile-frame.png';
import mobileLaptopUp1 from '../../../assets/images/wrong-mobile-frame.png';
import mobileLaptopUp2 from '../../../assets/images/wright-mobile-frame.png';
import prevArrow from '../../../assets/svgs/prev-arrow.svg';
import prevArrowDisable from '../../../assets/svgs/prev-arrow-disable.svg';
import nextArrow from '../../../assets/svgs/next-arrow.svg';
import nextArrowDisable from '../../../assets/svgs/next-arrow-disable.svg';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TryDemoTimeLine from '../try-demo-time-line';
import { useWindowDimensions } from '../../../hooks';
import { screenSizes, IN_PERFORMANCE_ACTION_TYPES } from '../../../constants/constants';
import { trackEvent } from '../../../services/analytics';
import { getInPerformanceTrackingProps } from '../../../zustand/session-metadata-store';
import tw from 'twin.macro';
import { css } from 'styled-components';

const IMAGE_DURATION = 4000;

// 100vh is the iOS Safari *static* viewport — taller than what's actually
// visible with the toolbar shown, which pushed the Exercise Now button below
// the fold and forced a page scroll to reach it. dvh tracks the visible
// viewport; the vh line is the fallback for engines without dvh and must stay
// a separate declaration, since a `tw` class pair collapses into one height key.
const pageHeight = css`
  height: 100vh;
  height: 100dvh;
`;

// Same fallback technique, for the portrait-mobile image panel's 65% height.
const imageHeight = css`
  height: 65vh;
  height: 65dvh;
`;

const ThirdSection = ({ onExerciseNow }) => {
  const { t } = useTranslation('try-demo');
  const [numberOfIndex, setNumberOfIndex] = useState(0);
  const { clientWindowWidth, clientWindowHeight } = useWindowDimensions();

  const images =
    clientWindowWidth > screenSizes.md
      ? [LaptopUp1, LaptopUp, LaptopUp2]
      : [MobileImage, mobileLaptopUp1, mobileLaptopUp, mobileLaptopUp2];

  const lastIndex = images.length - 1;
  const isMobile = clientWindowWidth <= screenSizes.md;
  // The image panel only shrinks to 65% height in portrait below `lg` — in
  // landscape or at `lg`+ it stays h-full, matching the previous CSS-only
  // `max-lg:[@media(orientation:landscape)]:h-full lg:h-full` behavior.
  const isDesktopHeight = clientWindowWidth >= screenSizes.lg;
  const isPortraitMobileHeight = !isDesktopHeight && clientWindowHeight > clientWindowWidth;

  useEffect(() => {
    // Earlier slides: auto-advance to the next image after its duration.
    if (numberOfIndex < lastIndex) {
      const timeout = setTimeout(() => {
        setNumberOfIndex(prev => (prev < lastIndex ? prev + 1 : prev));
      }, IMAGE_DURATION);

      return () => clearTimeout(timeout);
    }

    // Last slide: once its timeline plays out, move on to the exercise. Tying
    // this to the last image finishing (not a fixed page timeout) means going
    // back to an earlier slide cancels this timer and only re-arms it when the
    // user reaches the last image again — so we never transition early.
    const timeout = setTimeout(() => {
      onExerciseNow();
    }, IMAGE_DURATION);

    return () => clearTimeout(timeout);
  }, [numberOfIndex, lastIndex, onExerciseNow]);

  const goToPrev = () => setNumberOfIndex(prev => (prev > 0 ? prev - 1 : prev));
  const goToNext = () => setNumberOfIndex(prev => (prev < lastIndex ? prev + 1 : prev));

  const handleExerciseNowClick = () => {
    trackEvent('INPERFORMANCE_ACTION', {
      ...getInPerformanceTrackingProps(),
      action_type: IN_PERFORMANCE_ACTION_TYPES.EXERCISE_NOW,
    });
    onExerciseNow();
  };

  return (
    <div
      css={[
        tw`w-screen overflow-hidden grid grid-cols-1 grid-rows-[auto_auto_minmax(0,1fr)] max-lg:[@media(orientation:landscape)]:grid-cols-2 max-lg:[@media(orientation:landscape)]:grid-rows-none lg:grid-cols-[1fr_1fr] lg:grid-rows-none`,
        pageHeight,
      ]}
      style={{ fontFamily: 'Roboto, sans-serif' }}
    >
      <style>{`
        @keyframes trydemo-fade-in {
          from { opacity: 0; transform: scale(1.03); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
      {/* Preload images — invisible and unsized on purpose (just priming the
          cache), but `position: absolute` still paints them above normal-flow
          content regardless of DOM order, so pointer-events-none keeps them
          from ever intercepting clicks meant for the real UI underneath. */}
      <img
        loading="lazy"
        decoding="async"
        src={clientWindowWidth > screenSizes.md ? LaptopUp1 : mobileLaptopUp1}
        tw="absolute opacity-0 left-0 pointer-events-none"
        alt=""
      />
      <img
        loading="lazy"
        decoding="async"
        src={clientWindowWidth > screenSizes.md ? LaptopUp : mobileLaptopUp}
        tw="absolute opacity-0 left-0 pointer-events-none"
        alt=""
      />
      <img
        loading="lazy"
        decoding="async"
        src={clientWindowWidth > screenSizes.md ? LaptopUp2 : mobileLaptopUp2}
        tw="absolute opacity-0 left-0 pointer-events-none"
        alt=""
      />

      <div
        css={[
          tw`relative w-full overflow-hidden`,
          isPortraitMobileHeight ? imageHeight : tw`h-full`,
        ]}
        style={{ backgroundColor: isMobile ? '#ffffff' : '#fbffed' }}
      >
        <img
          loading="lazy"
          decoding="async"
          key={numberOfIndex}
          src={images[numberOfIndex]}
          alt=""
          tw="w-full h-full object-fill"
          style={{
            animation: 'trydemo-fade-in 0.75s ease-out both',
          }}
        />
        <TryDemoTimeLine duration={IMAGE_DURATION} resetKey={numberOfIndex} />
      </div>

      <div tw="flex w-full flex-col bg-white min-h-0 h-full overflow-y-auto">
        <div tw="flex flex-col items-start justify-center h-full px-5 py-5 max-lg:[@media(orientation:landscape)]:p-5 lg:p-10">
          <p tw="w-full font-semibold text-[20px] leading-[1.4] max-lg:[@media(orientation:landscape)]:text-[24px] max-lg:[@media(orientation:landscape)]:leading-[1.5] lg:text-[34px] lg:leading-[1.5] text-[#2e2e2e]">
            {t(isMobile ? 'thirdSection.instructionsMobile' : 'thirdSection.instructionsDesktop')}
          </p>
        </div>

        <div tw="hidden lg:flex items-center justify-between border-t border-[#d9dfe4] bg-[#f8f9fa] p-6">
          <div tw="flex items-center gap-6">
            <button
              type="button"
              onClick={goToPrev}
              disabled={numberOfIndex === 0}
              aria-label={t('thirdSection.prevAriaLabel')}
            >
              <img
                loading="lazy"
                decoding="async"
                src={numberOfIndex === 0 ? prevArrowDisable : prevArrow}
                tw="h-12 w-12 rtl:scale-x-[-1]"
                alt=""
              />
            </button>
            <button
              type="button"
              onClick={goToNext}
              disabled={numberOfIndex === lastIndex}
              aria-label={t('thirdSection.nextAriaLabel')}
            >
              <img
                loading="lazy"
                decoding="async"
                src={numberOfIndex === lastIndex ? nextArrowDisable : nextArrow}
                tw="h-12 w-12 rtl:scale-x-[-1]"
                alt=""
              />
            </button>
          </div>

          <button
            type="button"
            onClick={handleExerciseNowClick}
            tw="rounded-[6px] border border-[#BDC4C9] bg-[#c6d86e] px-6 py-3 text-[15px] font-medium text-black"
          >
            {t('thirdSection.exerciseNow')}
          </button>
        </div>

        {/* Mobile equivalent of the desktop footer button above — kept outside
            the per-slide image region so it stays visible across every slide,
            regardless of the current numberOfIndex. */}
        <div tw="lg:hidden px-5 pb-5 pt-2">
          <button
            type="button"
            onClick={handleExerciseNowClick}
            tw="w-full rounded-[6px] border-[1.5px] border-[#718122] bg-[#c6d86e] px-6 py-3 text-[15px] font-semibold text-[#2e2e2e]"
          >
            {t('thirdSection.exerciseNowMobile')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThirdSection;
